import type { FlushQueueResult, FulfillmentRunResult, MaintenanceState, PennylaneRunResult } from '@flux/shared';
import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { queryKeys } from '@/lib/query/keys.js';
import { toast } from '@/lib/toast.js';
import { disableMaintenanceMode, flushMaintenanceQueue, runFulfillmentNow, runPennylaneNow } from './api.js';

/** « Lancer le suivi Martelet » (`02` §11, aussi accessible depuis le tableau de bord). */
export function useRunFulfillmentNow(): UseMutationResult<FulfillmentRunResult, unknown, void> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: runFulfillmentNow,
    onSuccess: (result) => {
      if (result.skipped) {
        toast.error(result.reason ?? 'Le run a été ignoré.');
      } else if (result.status === 'failed') {
        toast.error(result.error ?? 'Le run du Suivi Martelet a échoué.');
      } else {
        toast.success('Suivi Martelet lancé.');
      }
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
    onError: (error: unknown) => {
      toast.fromError(error);
    },
  });
}

/** « Lancer Pennylane » (`02` §11, aussi accessible depuis le tableau de bord). */
export function useRunPennylaneNow(): UseMutationResult<PennylaneRunResult, unknown, void> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: runPennylaneNow,
    onSuccess: (result) => {
      if (result.status === 'failed') {
        toast.error(result.error ?? 'Le run Pennylane a échoué.');
      } else if (result.status === 'skipped') {
        toast.error('Le run Pennylane a été ignoré.');
      } else {
        toast.success('Run Pennylane lancé.');
      }
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
    onError: (error: unknown) => {
      toast.fromError(error);
    },
  });
}

/** « Envoyer maintenant » les fichiers en attente (`02` §2, §12.2). */
export function useFlushQueue(): UseMutationResult<FlushQueueResult, unknown, void> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: flushMaintenanceQueue,
    onSuccess: (result) => {
      toast.success(
        result.testMode ? 'Mode test actif : les fichiers restent en attente.' : result.queuedCount > 0
          ? `${String(result.queuedCount)} fichier(s) en cours d'envoi.`
          : 'Aucun fichier en file à envoyer.',
      );
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
    onError: (error: unknown) => {
      toast.fromError(error);
    },
  });
}

/**
 * « Reprendre les envois » vers Martelet (`02` §2 : bouton du bandeau). Invalide
 * aussi `queryKeys.settings.maintenance` (fabrique centrale, `lib/query/keys.ts`)
 * pour que le bandeau global de `AppShell` (`components/layout/maintenance-banner.tsx`,
 * polling 30 s) reflète le changement sans attendre son prochain cycle.
 */
export function useDisableMaintenance(): UseMutationResult<MaintenanceState, unknown, void> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: disableMaintenanceMode,
    onSuccess: () => {
      toast.success('Envois vers Martelet repris.');
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.settings.maintenance() });
    },
    onError: (error: unknown) => {
      toast.fromError(error);
    },
  });
}
